import { useState, useEffect } from "react";
import { X, Check, AlertTriangle, MapPin } from "lucide-react";
import { Card } from "./Card";
import { Btn } from "./Btn";
import { ATTENDANCE_STATUS_META } from "../../lib/attendance";

/* Admin override of any attendance day. Preserves clock-in/out, selfies,
   and location text the employee had captured. */
export function AttendanceOverrideModal({ target, onMark, onClose }) {
  const [status, setStatus] = useState("present");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (target) {
      setStatus(target.existing?.status || "present");
      setReason(target.existing?.reason || "");
    }
  }, [target]);

  if (!target) return null;

  const save = () => {
    const entry = { date: target.date, status, markedBy: "admin" };
    if (reason.trim()) entry.reason = reason.trim();
    if (target.existing?.clockInTime) entry.clockInTime = target.existing.clockInTime;
    if (target.existing?.clockOutTime) entry.clockOutTime = target.existing.clockOutTime;
    if (target.existing?.clockInAt) entry.clockInAt = target.existing.clockInAt;
    if (target.existing?.clockOutAt) entry.clockOutAt = target.existing.clockOutAt;
    if (target.existing?.clockInSelfie) entry.clockInSelfie = target.existing.clockInSelfie;
    if (target.existing?.clockOutSelfie) entry.clockOutSelfie = target.existing.clockOutSelfie;
    if (target.existing?.selfie) entry.selfie = target.existing.selfie;
    if (target.existing?.location) entry.location = target.existing.location;
    if (target.existing?.clockInLocation) entry.clockInLocation = target.existing.clockInLocation;
    if (target.existing?.clockOutLocation) entry.clockOutLocation = target.existing.clockOutLocation;
    onMark(target.name, entry);
    onClose();
  };

  const niceDate = new Date(target.date).toLocaleDateString("en-IN", {
    weekday: "long", day: "numeric", month: "long", year: "numeric"
  });

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <Card className="w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-base font-semibold text-slate-900">Override attendance</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-slate-500 mb-4">{target.name} · {niceDate}</p>

        <label className="text-xs font-medium text-slate-600 mb-1.5 block">Status</label>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5 mb-4">
          {Object.entries(ATTENDANCE_STATUS_META).map(([k, m]) => (
            <button key={k} onClick={() => setStatus(k)}
              className={`text-xs font-medium px-2 py-1.5 rounded-lg transition-colors ${
                status === k
                  ? `${m.cls} ring-2 ring-inset`
                  : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"
              }`}>
              {m.label}
            </button>
          ))}
        </div>

        <label className="text-xs font-medium text-slate-600 mb-1.5 block">Reason / note (optional)</label>
        <input value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Approved leave, sick day"
          className="w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 focus:outline-none focus:ring-blue-500 mb-4" />

        {(target.existing?.selfie || target.existing?.clockInSelfie || target.existing?.clockInLocation || target.existing?.location) && (
          <div className="mb-4 flex gap-3 items-start">
            {(target.existing?.clockInSelfie || target.existing?.selfie) && (
              <img src={target.existing?.clockInSelfie || target.existing?.selfie}
                alt="Submitted selfie"
                className="w-24 h-24 rounded-lg object-cover ring-1 ring-slate-200 shrink-0" />
            )}
            <div className="text-xs text-slate-600 leading-relaxed">
              <div className="font-medium text-slate-700 mb-1">Employee submission</div>
              {target.existing?.clockInTime && <div>Clock-in: <span className="font-medium">{target.existing.clockInTime}</span></div>}
              {(target.existing?.clockInLocation || target.existing?.location) && (
                <div className="mt-1 inline-flex items-start gap-1">
                  <MapPin className="w-3 h-3 mt-0.5 text-slate-400 shrink-0" />
                  <span>{target.existing?.clockInLocation || target.existing?.location}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="p-3 rounded-lg bg-amber-50/40 ring-1 ring-inset ring-amber-200 text-xs text-amber-900 leading-relaxed mb-4 flex gap-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          Overriding attendance is logged in the audit trail. Salary calculations refresh immediately.
        </div>

        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" onClick={save}><Check className="w-4 h-4" />Save override</Btn>
        </div>
      </Card>
    </div>
  );
}
